import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { WelcomeFrame } from "@/components/voice-onboarding/WelcomeFrame";
import { useOnboarding } from "@/contexts/voice-onboarding/OnboardingContext";
import { ONBOARD_ROUTES, VOICE_ONBOARD_STEPS } from "./steps";
import { LiveTestStage } from "./LiveTestStage";
import { TestStage } from "./TestStage";

/**
 * Step 6 of 7 — Test it live.
 *
 * The genie is real by this point, so this talks to the REAL genie: its own
 * public page, embedded, which is exactly what a customer gets. It used to run
 * a scripted client-side session that never touched a genie at all.
 *
 * The scripted stage survives as an honest fallback for the one case it is
 * needed: the genie's public page does not exist yet (or creation failed
 * upstream). It is labelled a preview there, not passed off as the real thing.
 */
const TestPage = () => {
  const navigate = useNavigate();
  const { draft, setDraft } = useOnboarding();

  const markTested = useCallback(() => {
    setDraft((d) => (d.tested ? d : { ...d, tested: true }));
  }, [setDraft]);

  const isLive = Boolean(draft.urlName);

  return (
    <WelcomeFrame
      step={6}
      steps={VOICE_ONBOARD_STEPS}
      eyebrow="TAKE IT FOR A SPIN"
      title="Say hi to your genie"
      subtitle={
        isLive
          ? "This is your genie's real page, exactly what your customers will hear. Tap to talk, or type a message, and try asking it something."
          : "Your genie's shareable page is still being set up. Here is a preview of what the conversation looks like in the meantime."
      }
      continueLabel="Looks good, let's launch"
      onBack={() => navigate(ONBOARD_ROUTES[4])}
      onContinue={() => navigate(ONBOARD_ROUTES[6])}
      onSkip={() => navigate("/genies")}
      onSkipStep={() => navigate(ONBOARD_ROUTES[6])}
    >
      {isLive ? (
        <LiveTestStage urlName={draft.urlName as string} onTested={markTested} />
      ) : (
        <TestStage onTested={markTested} />
      )}
    </WelcomeFrame>
  );
};

export default TestPage;
